import {computed, inject} from '@angular/core';
import {Filter, Brand, Network, Period, EntityListData} from '@models/online-reputation.models';
import { patchState, signalStore, withComputed, withHooks, withState } from '@ngrx/signals';
import {Events, on, withEventHandlers, withReducer} from '@ngrx/signals/events';
import {
  applyFilter,
  clearAllFilters,
  clearFilter,
  clearPeriodFilter,
  filterApiEvents,
  NetworkFilterKey,
  toggleFilterDrawer
} from '@core/events/filter.events';
import { withLogger } from '@core/stores/features/logger.feature';
import { formatPeriodDate } from '@core/utils/period-range.util';
import { applicationInitialized, loadUserInfo } from '@core/events/application.events';
import {LocationsService} from '@core/services/locations.service';
import {KeycloakService} from '@core/services/keycloak.service';
import {switchMap} from 'rxjs';
import {mapResponse} from '@ngrx/operators';
export type FilterOptions = {
  tenant: string,
  userId: string,
  markets: string[];
  brands: string[];
  outlets: string[];
  dealers: string[];
  agents: string[];
  agentsMap: Record<string, string>;
};

export type NetworkFilterPill = {
  key: NetworkFilterKey;
  label: string;
  value: string;
};

export type PeriodFilterPill = {
  label: string;
  value: string;
};

const FILTER_STORAGE_KEY = 'online-reputation.filter';
const FILTER_USER_KEY = 'online-reputation.user';
const FILTER_BACKUP_KEY = 'online-reputation.filter.backup';
const DEFAULT_PERIOD_FILTER = 'current_month';
const NETWORK_FILTER_KEYS: NetworkFilterKey[] = ['brands', 'dealers', 'outlets', 'agents'];
const DEFAULT_TENANT = 'online_reputation';

const FILTER_LABELS: Record<NetworkFilterKey, string> = {
  brands: 'Brand',
  outlets: 'Outlet',
  dealers: 'Dealer',
  agents: 'Agent'
};

const FILTER_PERIODS: Record<string, string> = {
  free: 'Custom Period',
  current_month: 'Current Month',
  '3_months': 'Last 3 Months',
  '6_months': 'Last 6 Months',
  '12_months': 'Last 12 Months',
  quarter_to_date: 'Quarter to Date',
  semester_to_date: 'Semester to Date',
  year_to_date: 'Year to Date',
  last_year_month: 'Last Year Month',
  last_year_quarter: 'Last Year Quarter'
};

type FilterState = {
  filter: Filter;
  options: FilterOptions;
  isDrawerOpen: boolean;
};
const initialOptions: FilterOptions = {
  tenant: DEFAULT_TENANT,
  userId: '',
  markets: [],
  brands: [],
  outlets: [],
  dealers: [],
  agents: [],
  agentsMap: {}
};

const initialFilter: Filter = {
  network: {
    tenant: DEFAULT_TENANT,
    markets: [],
    brands: [],
    dealers: [],
    outlets: [],
    agents: []
  },
  period: calculatePeriodDates(DEFAULT_PERIOD_FILTER)
};

const initialState: FilterState = {
  filter: initialFilter,
  options: initialOptions,
  isDrawerOpen: false
};

function normalizePersistedFilter(filter: Partial<Filter> | null | undefined): Filter {
  const network = filter?.network;
  const period = filter?.period;
  const fallbackPeriod = calculatePeriodDates(DEFAULT_PERIOD_FILTER);
  const datePreset =
    typeof period?.datePreset === 'string' && period.datePreset in FILTER_PERIODS
      ? period.datePreset
      : DEFAULT_PERIOD_FILTER;

  // For preset periods (not 'free'), recalculate dates so stale cached values are never used
  const recalculatedPeriod = datePreset !== 'free' ? calculatePeriodDates(datePreset) : null;

  const currentFrom = recalculatedPeriod?.current.from
    ?? (period?.current?.from ? new Date(period.current.from) : fallbackPeriod.current.from);
  const currentTo = recalculatedPeriod?.current.to
    ?? (period?.current?.to ? new Date(period.current.to) : fallbackPeriod.current.to);
  const previousFrom = recalculatedPeriod?.previous?.from
    ?? (period?.previous?.from ? new Date(period.previous.from) : (fallbackPeriod.previous?.from ?? new Date()));
  const previousTo = recalculatedPeriod?.previous?.to
    ?? (period?.previous?.to ? new Date(period.previous.to) : (fallbackPeriod.previous?.to ?? new Date()));

  return {
    network: {
      tenant: network?.tenant ?? '',
      markets: Array.isArray(network?.markets) ? network.markets : [],
      brands: Array.isArray(network?.brands) ? network.brands : [],
      dealers: Array.isArray(network?.dealers) ? network.dealers : [],
      outlets: Array.isArray(network?.outlets) ? network.outlets : [],
      agents: Array.isArray(network?.agents) ? network.agents : []
    },
    period: {
      current: { from: currentFrom, to: currentTo },
      previous: { from: previousFrom, to: previousTo },
      datePreset
    }
  };
}

function readPersistedFilter(): Filter | null {
  try {
    const rawFilter = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!rawFilter) {
      return null;
    }

    return normalizePersistedFilter(JSON.parse(rawFilter) as Partial<Filter>);
  } catch {
    localStorage.removeItem(FILTER_STORAGE_KEY);
    return null;
  }
}

function persistFilter(filter: Filter): void {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
}

function persistUserId(userId: string): void {
  try {
    if (userId) {
      localStorage.setItem(FILTER_USER_KEY, String(userId));
    } else {
      localStorage.removeItem(FILTER_USER_KEY);
    }
  } catch {
    // ignore
  }
}

function readPersistedUserId(): string | null {
  try {
    return localStorage.getItem(FILTER_USER_KEY);
  } catch {
    return null;
  }
}

function clearPersistedFilter(): void {
  localStorage.removeItem(FILTER_STORAGE_KEY);
  localStorage.removeItem(FILTER_USER_KEY);
}

function persistBackupFilter(filter: Filter): void {
  try {
    localStorage.setItem(FILTER_BACKUP_KEY, JSON.stringify(filter));
  } catch {
    // ignore
  }
}

function readBackupFilter(): Filter | null {
  try {
    const raw = localStorage.getItem(FILTER_BACKUP_KEY);
    if (!raw) return null;
    return normalizePersistedFilter(JSON.parse(raw) as Partial<Filter>);
  } catch {
    localStorage.removeItem(FILTER_BACKUP_KEY);
    return null;
  }
}

function clearBackupFilter(): void {
  try { localStorage.removeItem(FILTER_BACKUP_KEY); } catch { /* ignore */ }
}

function getStartOfMonth(date: Date = new Date()): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfMonth(date: Date = new Date()): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getStartOfQuarter(date: Date = new Date()): Date {
  const d = new Date(date);
  const quarter = Math.floor(d.getMonth() / 3);
  d.setMonth(quarter * 3, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfQuarter(date: Date = new Date()): Date {
  const d = new Date(date);
  const quarter = Math.floor(d.getMonth() / 3);
  d.setMonth(quarter * 3 + 3, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getStartOfYear(date: Date = new Date()): Date {
  const d = new Date(date);
  d.setMonth(0, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfYear(date: Date = new Date()): Date {
  const d = new Date(date);
  d.setMonth(11, 31);
  d.setHours(23, 59, 59, 999);
  return d;
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function formatDate(date: Date): string {
  return formatPeriodDate(date);
}

function calculatePeriodDates(periodType: string): Period {
  const today = new Date();
  let currentFrom: Date;
  let currentTo: Date;
  let previousFrom: Date;
  let previousTo: Date;

  switch (periodType) {
    case 'current_month': {
      currentFrom = getStartOfMonth(today);
      currentTo = today;
      previousFrom = getStartOfMonth(addMonths(today, -1));
      previousTo = getEndOfMonth(addMonths(today, -1));
      break;
    }
    case '3_months': {
      currentTo = today;
      currentFrom = getStartOfMonth(addMonths(today, -2));
      previousTo = getEndOfMonth(addMonths(today, -3));
      previousFrom = getStartOfMonth(addMonths(today, -5));
      break;
    }
    case '6_months': {
      currentTo = today;
      currentFrom = getStartOfMonth(addMonths(today, -5));
      previousTo = getEndOfMonth(addMonths(today, -6));
      previousFrom = getStartOfMonth(addMonths(today, -11));
      break;
    }
    case '12_months': {
      currentTo = today;
      currentFrom = getStartOfMonth(addMonths(today, -11));
      previousTo = getEndOfMonth(addMonths(today, -12));
      previousFrom = getStartOfMonth(addMonths(today, -23));
      break;
    }
    case 'quarter_to_date': {
      currentFrom = getStartOfQuarter(today);
      currentTo = today;
      previousFrom = getStartOfQuarter(addMonths(today, -3));
      previousTo = getEndOfQuarter(addMonths(today, -3));
      break;
    }
    case 'semester_to_date': {
      currentFrom = today.getMonth() < 6 ? getStartOfYear(today) : new Date(today.getFullYear(), 6, 1);
      currentTo = today;
      previousFrom = today.getMonth() < 6 ? new Date(today.getFullYear() - 1, 6, 1) : getStartOfYear(today);
      previousTo = getEndOfMonth(addMonths(currentFrom, -1));
      break;
    }
    case 'year_to_date': {
      currentFrom = getStartOfYear(today);
      currentTo = today;
      previousFrom = getStartOfYear(addMonths(today, -12));
      previousTo = getEndOfYear(addMonths(today, -12));
      break;
    }
    case 'last_year_month': {
      const lastYearToday = addMonths(today, -12);
      currentFrom = getStartOfMonth(lastYearToday);
      currentTo = getEndOfMonth(lastYearToday);
      const twoYearsAgo = addMonths(today, -24);
      previousFrom = getStartOfMonth(twoYearsAgo);
      previousTo = getEndOfMonth(twoYearsAgo);
      break;
    }
    case 'last_year_quarter': {
      const lastYearToday = addMonths(today, -12);
      currentFrom = getStartOfQuarter(lastYearToday);
      currentTo = getEndOfQuarter(lastYearToday);
      const twoYearsAgo = addMonths(today, -24);
      previousFrom = getStartOfQuarter(twoYearsAgo);
      previousTo = getEndOfQuarter(twoYearsAgo);
      break;
    }
    case 'free':
    default: {
      currentFrom = getStartOfMonth(today);
      currentTo = today;
      previousFrom = getStartOfMonth(addMonths(today, -1));
      previousTo = getEndOfMonth(addMonths(today, -1));
      break;
    }
  }

  return {
    current: { from: currentFrom, to: currentTo },
    previous: { from: previousFrom, to: previousTo },
    datePreset: periodType
  };
}

function buildPeriodFilterPill(period: Period): PeriodFilterPill | null {
  const periodType = period?.datePreset ?? DEFAULT_PERIOD_FILTER;

  if (periodType === 'free') {
    if (!period?.current?.from || !period?.current?.to) {
      return null;
    }

    return {
      label: 'Period',
      value: `${ formatDate(period.current.from) } to ${ formatDate(period.current.to) }`
    };
  }

  return {
    label: 'Period',
    value: FILTER_PERIODS[periodType] ?? FILTER_PERIODS[DEFAULT_PERIOD_FILTER]
  };
}

const BRAND_VALUES = new Set<string>(Object.values(Brand));

function buildSelectedPills(filter: Network, agentsMap?: Record<string, string>): NetworkFilterPill[] {
  const pills: NetworkFilterPill[] = [];

  const agentMapLookup = agentsMap ?? {};

  for (const key of NETWORK_FILTER_KEYS) {
    const values = filter[key] as unknown[] | undefined;
    if (!values?.length) {
      continue;
    }

    let valueStr: string;
    if (key === 'agents') {
      // Map agent ids to user-friendly names when available via agentsMap
      valueStr = values.map((v) => agentMapLookup[String(v)] ?? String(v)).join(', ');
    } else {
      valueStr = values.map((value) => String(value)).join(', ');
    }

    pills.push({
      key,
      label: FILTER_LABELS[key],
      value: valueStr
    });
  }

  return pills;
}

function parseUserInfoIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value !== 'string') {
    return [];
  }

  const normalized = value.trim();
  if (!normalized) {
    return [];
  }

  try {
    const parsed = JSON.parse(normalized) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // Fallback to CSV/single-value parsing when claim is not JSON.
  }

  return normalized.includes(',')
    ? normalized.split(',').map((item) => item.trim()).filter(Boolean)
    : [normalized];
}

function isFilterEmpty(filter: Filter): boolean {
  const network = filter.network;
  return (network?.markets?.length ?? 0) === 0
    && (network?.brands?.length ?? 0) === 0
    && (network?.dealers?.length ?? 0) === 0
    && (network?.outlets?.length ?? 0) === 0
    && (network?.agents?.length ?? 0) === 0;
}

export const FilterStore = signalStore(
  { providedIn: 'root' },
  withState<FilterState>(initialState),
  withLogger('FilterStore'),
  withComputed((store) => ({
    // Count the number of active filter categories (brands/dealers/outlets/agents)
    // rather than summing individual selections. The UI treats a category with
    // any selection as a single active filter for display purposes.
    activeFilterCount: computed(() => {
      const network = store.filter().network;
      return (network?.brands?.length ? 1 : 0)
        + (network?.dealers?.length ? 1 : 0)
        + (network?.outlets?.length ? 1 : 0)
        + (network?.agents?.length ? 1 : 0);
    }),
    // Pass agentsMap so pills can display agent names instead of raw ids
    selectedPills: computed(() => buildSelectedPills(store.filter().network ?? initialFilter.network!, store.options().agentsMap)),
    selectedPeriodPill: computed(() => buildPeriodFilterPill(store.filter().period ?? initialFilter.period!))
  })),
  withReducer(
    // When the application initializes we merge persisted values with current state but
    // avoid overwriting non-empty existing selections with empty persisted arrays.
    on(applicationInitialized, ({ payload: filter }, state): Partial<FilterState> => {
      const incoming = normalizePersistedFilter(filter);
      const current = state.filter ?? initialFilter;

      const nextNetwork = {
        tenant: incoming.network?.tenant || current.network?.tenant || '',
        markets: (incoming.network?.markets?.length ? incoming.network.markets : (current.network?.markets ?? [])),
        brands: (incoming.network?.brands?.length ? incoming.network.brands : (current.network?.brands ?? [])),
        dealers: (incoming.network?.dealers?.length ? incoming.network.dealers : (current.network?.dealers ?? [])),
        outlets: (incoming.network?.outlets?.length ? incoming.network.outlets : (current.network?.outlets ?? [])),
        agents: (incoming.network?.agents?.length ? incoming.network.agents : (current.network?.agents ?? []))
      };

      const nextFilter: Filter = {
        ...current,
        network: nextNetwork,
        period: incoming.period || current.period
      };

      persistFilter(nextFilter);
      return { filter: nextFilter };
    }),
    on(applyFilter, ({ payload: filter }, state) => {
      const nextFilter = normalizePersistedFilter(filter);

      // Determine defaults (prefer user-provided options, then fall back to
      // existing in-memory or application initial values)
      const defaultBrands = (Array.isArray(state.options?.brands) && state.options.brands.length > 0)
        ? state.options.brands
        : (state.filter?.network?.brands ?? initialFilter.network?.brands ?? []);

      const defaultDealers = (Array.isArray(state.options?.dealers) && state.options.dealers.length > 0)
        ? state.options.dealers
        : (state.filter?.network?.dealers ?? initialFilter.network?.dealers ?? []);

      const defaultOutlets = (Array.isArray(state.options?.outlets) && state.options.outlets.length > 0)
        ? state.options.outlets
        : (state.filter?.network?.outlets ?? initialFilter.network?.outlets ?? []);

      // Ensure brands/dealers/outlets are not emptied when initial values exist
      if ((nextFilter.network?.brands?.length ?? 0) === 0 && defaultBrands.length > 0) {
        nextFilter.network = { ...(nextFilter.network ?? {}), brands: [...defaultBrands] } as Network;
      }

      if ((nextFilter.network?.dealers?.length ?? 0) === 0 && defaultDealers.length > 0) {
        nextFilter.network = { ...(nextFilter.network ?? {}), dealers: [...defaultDealers] } as Network;
      }

      if ((nextFilter.network?.outlets?.length ?? 0) === 0 && defaultOutlets.length > 0) {
        nextFilter.network = { ...(nextFilter.network ?? {}), outlets: [...defaultOutlets] } as Network;
      }

      persistFilter(nextFilter);
      return { filter: nextFilter };
    }),
    on(loadUserInfo,
      ({ payload }, state): Partial<FilterState> => {
        // Support multiple possible claim names for tenant (typoed and correct variants)
        const tenantKeys = ['tenat_id', 'tenant-id', 'tenant_id', 'tenant', 'tenantId'];

        function extractTenant(payloadObj: Record<string, unknown> | undefined): string | undefined {
          if (!payloadObj) return undefined;
          for (const k of tenantKeys) {
            const raw = (payloadObj as Record<string, unknown>)[k];
            const parsed = parseUserInfoIds(raw);
            if (parsed.length > 0) return parsed[0];
          }
          return undefined;
        }

        // Try top-level, then attributes map (Keycloak sometimes puts custom claims there),
        // then try JSON-serialized fields as a last resort.
        const parsedTenant = (
          extractTenant(payload as Record<string, unknown>)
          || extractTenant((payload as Record<string, unknown>)['attributes'] as Record<string, unknown> | undefined)
          || ((): string | undefined => {
            // Sometimes claims are provided as a JSON string in a single property
            for (const k of tenantKeys) {
              const candidate = (payload as Record<string, unknown>)[k];
              if (typeof candidate === 'string') {
                const parsed = parseUserInfoIds(candidate);
                if (parsed.length > 0) return parsed[0];
              }
            }
            return undefined;
          })()
        );
        const outlets = parseUserInfoIds(payload['outlet_ids']);
        const dealers = parseUserInfoIds(payload['dealer_ids']);
        const brands = parseUserInfoIds(payload['brand_ids'])
          .map((brandId) => brandId.trim())
          .filter((brandId): brandId is Brand => BRAND_VALUES.has(brandId.toUpperCase()));

        // If a persisted filter exists (applyFilter persists immediately) prefer that
        // when deciding whether the filter is "empty". This prevents a race where
        // an applyFilter was dispatched and persisted but the in-memory state hasn't
        // yet been updated when loadUserInfo runs.

        // Extract a stable user id from common claims so we can clear state when the
        // actual user changes (not just tenant).
        const userIdKeys = ['sub', 'user_id', 'id', 'preferred_username', 'username'];
        function extractUserId(payloadObj: Record<string, unknown> | undefined): string | undefined {
          if (!payloadObj) return undefined;
          for (const k of userIdKeys) {
            const raw = (payloadObj as Record<string, unknown>)[k];
            if (typeof raw === 'string' && raw.trim()) return raw.trim();
          }
          return undefined;
        }

        const parsedUserId = (
          extractUserId(payload as Record<string, unknown>)
          || extractUserId((payload as Record<string, unknown>)['attributes'] as Record<string, unknown> | undefined)
        ) ?? '';

        // If tenant is missing we must clear persisted + in-memory filters and
        // ensure options are overwritten. The actual logout is performed in an
        // event handler (side-effect) so reducer stays pure.
        if (!parsedTenant) {
          clearPersistedFilter();

          const clearedNetwork: Network = {
            tenant: '',
            markets: [],
            brands: [],
            dealers: [],
            outlets: [],
            agents: []
          };

          const nextFilter: Filter = {
            ...state.filter,
            network: clearedNetwork
          };

          // Overwrite options (always overwrite userId and tenant per requirements)
          const nextOptions: FilterOptions = {
            tenant: '',
            userId: '',
            markets: [],
            brands: [],
            outlets: [],
            dealers: [],
            agents: [],
            agentsMap: {}
          };

          persistFilter(nextFilter);
          persistUserId('');

          return {
            filter: nextFilter,
            options: nextOptions
          };
        }

        // Simplified deterministic user-switch rules:
        // - On first init/hydration we do NOT set options.userId (handled in onInit hook).
        // - When loadUserInfo arrives, compute parsedUserId and compare to previousUserId
        //   where previousUserId = state.options.userId || readPersistedUserId() || ''.
        // - If previousUserId is non-empty and parsedUserId !== previousUserId => real user switch:
        //     clear persisted filter and rebuild from the new user's info (replace selections)
        // - Else (previousUserId empty): first user load in this session => do not overwrite in-memory
        //     selections; only set options.userId and apply defaults when in-memory filter is empty.

        const previousUserId = readPersistedUserId() ?? '';

        const isRealUserSwitch = parsedUserId && previousUserId && parsedUserId !== previousUserId;

        let nextNetwork: Network;
        if (isRealUserSwitch) {
          // Clear previous persisted filter and rebuild filter from user profile
          nextNetwork = {
            tenant: parsedTenant,
            markets: [],
            brands: brands.length > 0 ? brands : [],
            dealers,
            outlets,
            agents: []
          };
          clearPersistedFilter();
        } else {
          // Not a real switch (either first load or same user) — preserve in-memory selections
          // but initialize defaults only when the in-memory filter is empty. Tenant is
          // always taken from the incoming payload (no preservation).
          const effectiveFilter = state.filter && !isFilterEmpty(state.filter) ? state.filter : (readPersistedFilter() ?? state.filter);
          const shouldInitializeAllFilters = isFilterEmpty(effectiveFilter);
          const currentBrands = effectiveFilter.network?.brands ?? [];
          const shouldDefaultBrands = currentBrands.length === 0 && brands.length > 0;

          nextNetwork = {
            ...state.filter.network,
            tenant: parsedTenant,
            ...(shouldInitializeAllFilters ? { outlets, dealers } : {}),
            ...(shouldDefaultBrands ? { brands } : {})
          };
        }

        const nextFilter: Filter = {
          ...state.filter,
          network: nextNetwork
        };

        // Update options: always overwrite tenant and userId per requirements
        const nextOptions: FilterOptions = {
          tenant: parsedTenant,
          userId: parsedUserId,
          markets: [],
          outlets,
          dealers,
          brands,
          agents: [],
          agentsMap: {}
        };

        // If a backup filter exists (from a previous clear when user options
        // were not available) restore it now and clear the backup so the
        // user's prior selections are returned when their info becomes
        // available.
        const backed = readBackupFilter();
        if (backed) {
          clearBackupFilter();
          // Ensure tenant reflects parsedTenant (user may have logged in)
          const restored: Filter = {
            ...backed,
            network: {
              ...backed.network,
              tenant: parsedTenant
            }
          };
          persistFilter(restored);
          persistUserId(parsedUserId);
          return { filter: restored, options: nextOptions };
        }

        // Persist filter and user id
        persistFilter(nextFilter);
        persistUserId(parsedUserId);

        return {
          filter: nextFilter,
          options: nextOptions
        };
      }),
    on(toggleFilterDrawer, (_, state) => {
      console.debug('[FilterStore] toggleFilterDrawer - previous:', state.isDrawerOpen, 'next:', !state.isDrawerOpen);
      return { isDrawerOpen: !state.isDrawerOpen };
    }),
    on(clearFilter, ({ payload }, state) => {
      const key = payload.key as NetworkFilterKey;
      const nextNetwork = { ...state.filter.network };

      switch (key) {
        case 'brands':
          nextNetwork.brands = [];
          break;
        case 'dealers':
          nextNetwork.dealers = [];
          break;
        case 'outlets':
          nextNetwork.outlets = [];
          break;
        case 'agents':
          nextNetwork.agents = [];
          break;
      }

      const nextFilter: Filter = {
        ...state.filter,
        network: nextNetwork
      };

      persistFilter(nextFilter);
      return { filter: nextFilter };
    }),
    on(clearPeriodFilter, (_, state) => {
      const nextFilter: Filter = {
        ...state.filter,
        period: calculatePeriodDates(DEFAULT_PERIOD_FILTER)
      };

      persistFilter(nextFilter);
      return { filter: nextFilter };
    }),
    on(clearAllFilters, (_, state) => {
      // Clear should return the store to the "initialized" state derived
      // from the user's details (state.options). If user details are
      // available (brands/dealers/outlets/agents) restore those. If not,
      // preserve the current filter in a backup so it can be restored later
      // and fall back to the application's initial filter.

      const tenant = state.options?.tenant ?? initialFilter.network?.tenant ?? DEFAULT_TENANT;

      const optionsBrands = Array.isArray(state.options?.brands) ? state.options!.brands : [];
      const optionsDealers = Array.isArray(state.options?.dealers) ? state.options!.dealers : [];
      const optionsOutlets = Array.isArray(state.options?.outlets) ? state.options!.outlets : [];

      // Determine whether we have user-provided options to restore. Exclude
      // agents from this check because agents are not applied at init time.
      const hasUserOptions = optionsBrands.length > 0 || optionsDealers.length > 0 || optionsOutlets.length > 0;

      let nextNetwork: Network;
      if (hasUserOptions) {
        // Restore from user options
        nextNetwork = {
          tenant,
          markets: [],
          brands: optionsBrands,
          dealers: optionsDealers,
          outlets: optionsOutlets,
          // Do not map agents here — agents are handled via the locations API
          // and not applied during initialization.
          agents: []
        };

        // Clear any previously stored backup since we have authoritative options
        clearBackupFilter();
      } else {
        // No user options available: persist a backup of the current filter
        // so it can be restored later, then fall back to initial network
        // (preserving tenant) and default period.
        if (state.filter) {
          persistBackupFilter(state.filter);
        }

        nextNetwork = {
          tenant,
          markets: [],
          brands: [],
          dealers: [],
          outlets: [],
          agents: []
        };
      }

      const nextFilter: Filter = {
        network: nextNetwork,
        period: calculatePeriodDates(DEFAULT_PERIOD_FILTER)
      };

      // Replace persisted filter with the cleared/initialized filter so
      // hydration reflects the new state.
      persistFilter(nextFilter);

      return { filter: nextFilter };
    }),
    on(filterApiEvents.loadLocationsSuccess, ({ payload }, state) => {

      const data = payload.data || [];

      // Store only agent ids in options.agents (to preserve API contract) and
      // populate agentsMap with id->display name for UI labels.
      const agents: string[] = data.map((entityListData: EntityListData) => String(entityListData.id));
      const agentsMap: Record<string, string> = (data || []).reduce((acc: Record<string, string>, entityListData: EntityListData) => {
        const id = String(entityListData.id);
        acc[id] = String((entityListData as any).name ?? entityListData.id);
        return acc;
      }, {} as Record<string, string>);

      const nextFilterOptions: FilterOptions = {
        ...state.options,
        agents,
        agentsMap
      };

      return {
        ...state,
        options: nextFilterOptions
      };
    })
  ),
  withEventHandlers(
    (
      store,
      events = inject(Events),
      locationService = inject(LocationsService)
    ) => ({
      loadLocations$: events
        .on(applyFilter, applicationInitialized)
        .pipe(
          switchMap(({ payload: filter }) =>
            locationService.list(filter).pipe(
              mapResponse({
                next: (paginatedData) => filterApiEvents.loadLocationsSuccess(paginatedData),
                error: (error: { message: string }) => {
                  // Use the error parameter to avoid unused-parameter warnings and log the failure.
                  console.error('Failed to load locations', error);
                  return undefined;
                },
              })
            )
          )
        ),
      // When loadUserInfo arrives with no tenant claims we must trigger a logout
      // as a side-effect. The reducer already clears persisted state for this
      // scenario; here we perform the external call to KeycloakService.logout().
      logoutOnMissingTenant$: events.on(loadUserInfo).pipe(
        switchMap(async ({ payload }) => {
          const tenantKeys = ['tenat_id', 'tenant-id', 'tenant_id', 'tenant', 'tenantId'];

          function extractTenant(payloadObj: Record<string, unknown> | undefined): string | undefined {
            if (!payloadObj) return undefined;
            for (const k of tenantKeys) {
              const raw = (payloadObj as Record<string, unknown>)[k];
              const parsed = parseUserInfoIds(raw);
              if (parsed.length > 0) return parsed[0];
            }
            return undefined;
          }

          const parsedTenant = (
            extractTenant(payload as Record<string, unknown>)
            || extractTenant((payload as Record<string, unknown>)['attributes'] as Record<string, unknown> | undefined)
            || ((): string | undefined => {
              for (const k of tenantKeys) {
                const candidate = (payload as Record<string, unknown>)[k];
                if (typeof candidate === 'string') {
                  const parsed = parseUserInfoIds(candidate);
                  if (parsed.length > 0) return parsed[0];
                }
              }
              return undefined;
            })()
          );

          //TODO:move this to on event and logic in the application store
          if (!parsedTenant) {
            try {
              // Perform optional injection at event-handling time so tests that don't
              // provide KeycloakService do not fail during TestBed setup.
              let keycloakService: KeycloakService | null = null;
              try {
                // inject may throw if no injector is present; catch to be safe
                // @ts-ignore
                keycloakService = inject(KeycloakService);
              } catch {
                keycloakService = null;
              }

              if (keycloakService) {
                await keycloakService.logout();
              }
            } catch (err) {
              console.error('Logout failed', err);
            }
          }

          return undefined;
        })
      )
    })
  ),
  withHooks({
    onInit(store) {
      const persistedFilter = readPersistedFilter();


      if (persistedFilter) {
        // Preserve the application's default tenant while restoring persisted selections
        // so tests and app expectations that rely on the default tenant remain valid.
        const mergedFilter: Filter = {
          ...persistedFilter,
          network: {
            ...persistedFilter.network,
            tenant: initialFilter.network?.tenant ?? DEFAULT_TENANT
          }
        };

        // Do NOT restore persisted user id into `options.userId` on hydration.
        // Treat persisted user id as storage for switch-detection only; the
        // in-memory `options.userId` should remain empty for the first
        // loadUserInfo in this session so we don't treat hydration as a user
        // switch. Persisted user id is still available via readPersistedUserId().
        const mergedOptions: FilterOptions = {
          ...initialOptions,
          userId: ''
        };

        patchState(store, { filter: mergedFilter, options: mergedOptions });
      }


    }
  })
);
